import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import PoolCard from "@/components/PoolCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Search as SearchIcon, SlidersHorizontal, Map as MapIcon } from "lucide-react";
import { AMENITIES } from "@/lib/i18n";

const makeIcon = (label) => L.divIcon({
  html: `<div style="background:#005C7A;color:#fff;padding:4px 10px;border-radius:16px;box-shadow:0 4px 12px rgba(0,92,122,0.35);font-weight:700;font-size:12px;font-family:Manrope,sans-serif;white-space:nowrap;transform:translate(-50%,-100%);border:2px solid #fff">${label}</div>`,
  className: "",
  iconSize: [0, 0],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) { map.setView([45.5, 10.5], 6); return; }
    if (points.length === 1) { map.setView(points[0], 11); return; }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
  }, [points, map]);
  return null;
}

export default function Search() {
  const { t } = useApp();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ city: "", min_price: "", max_price: "", amenities: [] });
  const [showMap, setShowMap] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filters.city) params.city = filters.city;
    if (filters.min_price) params.min_price = filters.min_price;
    if (filters.max_price) params.max_price = filters.max_price;
    if (filters.amenities.length) params.amenities = filters.amenities.join(",");
    const { data } = await api.get("/pools", { params });
    setPools(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleAmenity = (a) => {
    setFilters((f) => ({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] }));
  };

  const withCoords = useMemo(() => pools.filter((p) => p.lat && p.lng), [pools]);
  const points = useMemo(() => withCoords.map((p) => [p.lat, p.lng]), [withCoords]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#E27B58] mb-2">{t("search.explore")}</div>
          <h1 className="font-display text-4xl font-bold">{t("search.title")}</h1>
        </div>
        <button
          onClick={() => setShowMap((v) => !v)}
          data-testid="toggle-search-map"
          className="mt-1 px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 border bg-white hover:border-[#005C7A]/40"
        >
          <MapIcon className="w-3.5 h-3.5" /> {showMap ? "Nascondi mappa" : "Mostra mappa"}
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 h-fit bg-white border border-border rounded-2xl p-6 space-y-5">
          <div className="flex items-center gap-2 text-sm font-semibold"><SlidersHorizontal className="w-4 h-4" /> {t("search.filters")}</div>
          <div>
            <Label className="text-xs">{t("search.city")}</Label>
            <Input value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} placeholder={t("search.any")} data-testid="filter-city" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">{t("search.min")}</Label>
              <Input type="number" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} placeholder="€0" data-testid="filter-min" />
            </div>
            <div>
              <Label className="text-xs">{t("search.max")}</Label>
              <Input type="number" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} placeholder="€500" data-testid="filter-max" />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-2 block">{t("search.amenities")}</Label>
            <div className="flex flex-wrap gap-2">
              {AMENITIES.map((a) => (
                <button key={a} onClick={() => toggleAmenity(a)} data-testid={`filter-amenity-${a}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${filters.amenities.includes(a) ? "bg-[#005C7A] text-white border-[#005C7A]" : "border-border bg-white hover:border-[#005C7A]"}`}>
                  {t(`amenity.${a}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={load} className="flex-1 rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid="apply-filters"><SearchIcon className="w-4 h-4 mr-1" /> {t("search.apply")}</Button>
            <Button onClick={() => { setFilters({ city: "", min_price: "", max_price: "", amenities: [] }); setTimeout(load, 0); }} variant="outline" className="rounded-full" data-testid="reset-filters">{t("search.clear")}</Button>
          </div>
        </aside>
        <div>
          {showMap && (
            <div className="mb-6 bg-white border border-border rounded-2xl overflow-hidden h-[380px]" data-testid="search-map-container">
              {loading ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">Caricamento mappa…</div>
              ) : withCoords.length === 0 ? (
                <div className="flex items-center justify-center h-full text-muted-foreground text-sm px-6 text-center">Nessuna piscina con coordinate per questa ricerca.</div>
              ) : (
                <MapContainer center={[45.5, 10.5]} zoom={6} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
                  <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <FitBounds points={points} />
                  {withCoords.map((p) => (
                    <Marker key={p.pool_id} position={[p.lat, p.lng]} icon={makeIcon(`€${p.price_per_day}`)}>
                      <Popup minWidth={240}>
                        <div data-testid={`search-popup-${p.pool_id}`}>
                          {p.photos?.[0] && <img src={fileUrl(p.photos[0])} alt="" className="w-full h-24 object-cover rounded-md mb-2" />}
                          <div className="font-display text-sm font-bold leading-tight">{p.title}</div>
                          <div className="text-xs text-muted-foreground mb-2">{p.hotel_name} · {p.city}</div>
                          <div className="text-xs mb-2">Giornata intera: <strong>€{p.price_per_day}</strong></div>
                          <Link to={`/pool/${p.pool_id}`}>
                            <Button size="sm" className="w-full rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid={`search-popup-cta-${p.pool_id}`}>Vedi dettagli</Button>
                          </Link>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              )}
            </div>
          )}
          <p className="text-sm text-muted-foreground mb-6" data-testid="results-count">{pools.length} {t("search.results")}</p>
          {loading ? (
            <div className="text-muted-foreground">Loading...</div>
          ) : pools.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">{t("search.empty")}</div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {pools.map((p, i) => (
                <div key={p.pool_id} className="fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                  <PoolCard pool={p} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
